import { useRef, useState, useEffect, useCallback } from "react";
import { FaEraser, FaCheck, FaTimes } from "react-icons/fa";

// Lienzo para dibujar una firma a mano (mouse o dedo). Genera un PNG con fondo
// transparente, listo para usar en los recibos.
export default function SignaturePad({ onSave, onCancel }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [isEmpty, setIsEmpty] = useState(true);

  // Configura el contexto del canvas (trazo) una vez montado.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#1f2937";
  }, []);

  // Coordenadas del puntero relativas al bitmap del canvas (corrige escalado CSS).
  const pos = useCallback((e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (canvas.width / rect.width),
      y: (e.clientY - rect.top) * (canvas.height / rect.height),
    };
  }, []);

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = pos(e);
    canvasRef.current.setPointerCapture?.(e.pointerId);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (isEmpty) setIsEmpty(false);
  };

  const end = () => {
    drawing.current = false;
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    setIsEmpty(true);
  };

  const handleSave = () => {
    if (isEmpty) return;
    onSave(canvasRef.current.toDataURL("image/png"));
  };

  return (
    <div className="space-y-2">
      <div className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white overflow-hidden">
        <canvas
          ref={canvasRef}
          width={460}
          height={160}
          className="w-full h-40 touch-none cursor-crosshair block"
          style={{ backgroundImage: "linear-gradient(#fff,#fff)" }}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
        />
      </div>
      <div className="flex items-center justify-between">
        <p className="text-[11px] text-gray-400">Dibujá tu firma con el mouse o el dedo.</p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleClear}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            <FaEraser className="w-3 h-3" /> Borrar
          </button>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              <FaTimes className="w-3 h-3" /> Cancelar
            </button>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={isEmpty}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-primary hover:bg-secondary text-white rounded-lg disabled:opacity-50"
          >
            <FaCheck className="w-3 h-3" /> Usar firma
          </button>
        </div>
      </div>
    </div>
  );
}
